import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
	Title,
	Text,
	Paper,
	Box,
	Group,
	Loader,
	Center,
	Button,
	Stack,
	Pagination,
} from '@mantine/core'
import { getUserCheckInHistory } from '../services/actions/checkInActions'

const ExperienceUserCheckInHistory = () => {
	const { orgSlug, experienceSlug } = useParams()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const userId = searchParams.get('userId')

	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [history, setHistory] = useState([])
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(20)

	const currentExperience = useSelector(
		(state) => state.experience?.experienceDetails
	)

	useEffect(() => {
		let cancelled = false
		const detectDevice = () => (window.innerWidth < 768 ? 'mobile' : 'desktop')
		setPageSize(detectDevice() === 'mobile' ? 10 : 20)

		const fetchHistory = async () => {
			try {
				setLoading(true)
				setError('')
				setHistory([])
				setPage(1)
				const results = await getUserCheckInHistory(
					experienceSlug,
					orgSlug,
					userId,
				)
				if (!cancelled) setHistory(results)
			} catch (err) {
				console.error(err)
				if (!cancelled) setError('Failed to load history')
			} finally {
				if (!cancelled) setLoading(false)
			}
		}
		if (currentExperience?.id) {
			fetchHistory()
		}
		return () => {
			cancelled = true
		}
	}, [experienceSlug, orgSlug, currentExperience?.id, userId])

	const handleSelect = (checkIn) => {
		navigate(`/${orgSlug}/experiences/${experienceSlug}/checkIn/${checkIn.id}`)
	}

	if (!currentExperience?.id) {
		navigate(`/${orgSlug}/`)
		return null
	}

	const totalPages = Math.ceil(history.length / pageSize)
	const paginatedHistory = history.slice((page - 1) * pageSize, page * pageSize)
	const userName = userId && !loading ? history[0]?.checkedInBy?.name : null

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				{userName ? `${userName}'s Check-In History` : 'Check-In History'}
			</Title>

			{loading && (
				<Center>
					<Loader />
				</Center>
			)}

			{error && (
				<Text color='red' mb='sm'>
					{error}
				</Text>
			)}

			{!loading && !error && (
				<>
					<Text fw={500} mb='sm'>
						Total Check-Ins: {history.length}
					</Text>

					{history.length === 0 ? (
						<Text>No past check-ins found.</Text>
					) : (
						<>
							{totalPages > 1 && (
								<Box mb='sm'>
									<Pagination
										value={page}
										onChange={setPage}
										total={totalPages}
										size='sm'
									/>
								</Box>
							)}
							<Stack spacing='sm'>
								{paginatedHistory.map((checkIn) => (
									<Paper
										key={checkIn.id}
										withBorder
										p='sm'
										radius='md'
										shadow='xs'
									>
										<Group position='apart' align='flex-start'>
											<Box>
												<Text fw={600}>{checkIn.travelerName}</Text>
												<Text size='xs' c='dimmed'>
													{checkIn.travelerEmail}
												</Text>
												<Text size='xs' c='dimmed'>
													{checkIn.travelerPhone}
												</Text>
												<Text size='xs'>
													Checked in:{' '}
													{checkIn.actualCheckInDateTime
														? new Date(
																checkIn.actualCheckInDateTime
														  ).toLocaleString()
														: 'N/A'}
												</Text>
												<Text size='xs'>Station: {checkIn.stationName}</Text>
											</Box>
											<Button
												size='xs'
												variant='light'
												onClick={() => handleSelect(checkIn)}
											>
												View
											</Button>
										</Group>
									</Paper>
								))}
							</Stack>
						</>
					)}
				</>
			)}
		</Box>
	)
}

export default ExperienceUserCheckInHistory
